import { useCallback } from 'react';
import debounce from 'lodash/debounce';
import { getEdition } from '@jira/platform__application-editions/src/edition.tsx';
import type { ExperienceAttributes } from '@atlassian/jira-experience-tracker/src/common/types.tsx';
import { useExperienceFail } from '@atlassian/jira-experience-tracker/src/ui/experience-fail/index.tsx';
import { useExperienceStart } from '@atlassian/jira-experience-tracker/src/ui/experience-start/index.tsx';
import { useExperienceSuccess } from '@atlassian/jira-experience-tracker/src/ui/experience-success/index.tsx';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { useProjectContext } from '@atlassian/jira-providers-project-context/src/index.tsx';
import { getApplicationForProject } from '@atlassian/jira-shared-types/src/application.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useAppEditions } from '@atlassian/jira-tenant-context-controller/src/components/app-editions/index.tsx';
import { useIsEmbedMode } from '@atlassian/jira-issue-view-embed-mode/src/index.tsx';
import { useExperienceAbort } from '@atlassian/jira-experience-tracker/src/ui/experience-abort/index.tsx';
import { isAbortableError } from '@atlassian/jira-fetch/src/utils/is-abortable-error.tsx';
import type { IssueNavigatorViewId } from '../../common/types.tsx';
import { ANALYTICS_SOURCE, EXPERIENCE_NAME } from '../../common/constants.tsx';
/**
 * This debouncer is shared between both the success and fail calls so that the last one called in the current event
 * loop tick is the one that gets logged. This helps us overcome weaknesses in the Relay-based rendering where an error
 * being thrown taks an additional render cycle to reach the component managing the analytics.
 */
const sharedDebouncer = debounce((action) => action(), 0);

/**
 * Define the experience tracker analytics events for the issue navigator. These are used to mark the experience of
 * showing issue search results as a success or failure and is our key reliability SLO.
 */
export const useExperienceAnalytics = (
	/**
	 * Event emitted when page data has loaded and the key experience is interactive.
	 */
	onPageDataLoad: ((selectedView: IssueNavigatorViewId) => void) | null | undefined,
	/**
	 * Analytics attributes to be emitted as part of success/fail experience events.
	 */
	attributes?: ExperienceAttributes,
) => {
	let analyticsAttributes = attributes;

	const isEmbed = useIsEmbedMode();
	analyticsAttributes = {
		...analyticsAttributes,
		isEmbed,
	};
	if (fg('improve_nin_slo_events')) {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		const { data: project } = useProjectContext();

		// eslint-disable-next-line react-hooks/rules-of-hooks
		const appEditions = useAppEditions();
		const application = project?.projectType ? getApplicationForProject(project.projectType) : null;
		const edition = application ? getEdition(application, appEditions) : null;
		analyticsAttributes = {
			...analyticsAttributes,
			application,
			edition,
		};
	}

	const onExperienceStart = useExperienceStart({
		experience: EXPERIENCE_NAME,
		experienceId: undefined,
		analyticsSource: ANALYTICS_SOURCE,
	});

	const onExperienceSuccess = useExperienceSuccess({
		experience: EXPERIENCE_NAME,
		attributes: analyticsAttributes,
	});

	const onExperienceFail = useExperienceFail({
		experience: EXPERIENCE_NAME,
		attributes: analyticsAttributes,
	});
	const onExperienceAbort = useExperienceAbort({
		experience: EXPERIENCE_NAME,
		attributes: analyticsAttributes,
	});

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const onFireUIAnalyticsEvent = useCallback(() => {
		const event = createAnalyticsEvent({
			action: 'viewed',
			actionSubject: 'screen',
		});
		fireUIAnalytics(event, 'issueSearchResults', attributes ?? {});
	}, [attributes, createAnalyticsEvent]);

	const onIssueSearchSuccess = useCallback(
		(view: IssueNavigatorViewId) => {
			sharedDebouncer(() => {
				onExperienceStart();
				onExperienceSuccess();
				onFireUIAnalyticsEvent();
			});
			onPageDataLoad && onPageDataLoad(view);
		},
		[onExperienceStart, onExperienceSuccess, onFireUIAnalyticsEvent, onPageDataLoad],
	);

	const onIssueSearchFail = useCallback(
		(location: string, error: Error, view: IssueNavigatorViewId) => {
			sharedDebouncer(() => {
				onExperienceStart();
				if (isAbortableError(error) && fg('improve_abortable_errors_experience_tracker')) {
					onExperienceAbort(location, { errorMessage: error.message, location });
					return;
				}
				onExperienceFail(location, error);
			});
			onPageDataLoad && onPageDataLoad(view);
		},
		[onExperienceFail, onExperienceAbort, onExperienceStart, onPageDataLoad],
	);

	return {
		onIssueSearchSuccess,
		onIssueSearchFail,
	};
};
